/**
 * Utility functions for BorrowerBizProfile form logic
 */

import { Dispatch } from "@reduxjs/toolkit";
import {
  setCompanyRedux,
  setRegNumberRedux,
  setLegalStructureRedux,
  setTypeOfInstituteRedux,
  setAddressRedux,
  setPostalRedux,
  setWebsiteRedux,
  setIndustryGroupRedux,
  setIndustryDropdownValuesRedux,
  setIndustryOthersRedux,
  setCountryOfIncorpRedux,
  setCountryOfDomicileRedux,
  setPhoneNumber,
  setCompanyStartDateRedux,
} from "../../../features/merchant/biz";
import { BizProfileFormData, BizProfileFormErrors } from "../types";
import { REQUIRED_FIELD_ERRORS, INITIAL_ERRORS } from "../constants";
import { Industry, IndustryGroup } from "../../../constant/enum";
import { IndustryGroupMap } from "../../../constant/dropdownValues";
import {
  CountryCode,
  codeToCountryNameMap,
} from "@funded-here-interface/common/src/constant/country";

/**
 * Hydrates business profile data into Redux store
 */
export const hydrateBizProfileToRedux = (
  dispatch: Dispatch,
  data: BizProfileFormData
) => {
  dispatch(setCompanyRedux(data.companyName));
  dispatch(setRegNumberRedux(data.companyRegistrationNo));
  dispatch(setLegalStructureRedux(data.legalStructure));
  dispatch(setIndustryGroupRedux(data.industryGroup));
  dispatch(setIndustryDropdownValuesRedux(data.industryDropdownValues || []));
  dispatch(setIndustryOthersRedux(data.industryOthers || ""));
  dispatch(setTypeOfInstituteRedux(data.industry));
  dispatch(setCountryOfIncorpRedux(data.countryOfIncorporation));
  dispatch(setCountryOfDomicileRedux(data.countryOfDomicile));
  dispatch(setAddressRedux(data.legalEntityAddress));
  dispatch(setPostalRedux(data.postalCode));
  dispatch(setWebsiteRedux(data.website));
  dispatch(setPhoneNumber(data.phoneNumber));
  dispatch(setCompanyStartDateRedux(data.companyStartDate));
};

/**
 * Hydrates business profile data into local state setters
 */
export const hydrateBizProfileToState = (
  data: BizProfileFormData,
  setters: {
    setCompany: (value: string) => void;
    setRegNumber: (value: string) => void;
    setLegStr: (value: string) => void;
    setIndustryGroup: (value: string) => void;
    setIndustryDropdownValues: (value: Industry[]) => void;
    setInstitute: (value: string) => void;
    setIndustryOthers: (value: string) => void;
    setCountryCountryOfIncorp: (value: string) => void;
    setCountryCountryOfDomicile: (value: string) => void;
    setAddress: (value: string) => void;
    setPostalCode: (value: string) => void;
    setcompanysite: (value: string) => void;
    setPhone: (value: string) => void;
    setCompanyStartDate: (value: string) => void;
    setCountryCode: (value: CountryCode | undefined) => void;
  }
) => {
  setters.setCompany(data.companyName || "");
  setters.setRegNumber(data.companyRegistrationNo || "");
  setters.setLegStr(data.legalStructure || "");
  setters.setIndustryGroup(data.industryGroup || "");
  setters.setIndustryDropdownValues(
    IndustryGroupMap[data.industryGroup as IndustryGroup] || []
  );
  setters.setInstitute(data.industry || "");
  setters.setIndustryOthers(data.industryOthers || "");
  setters.setCountryCountryOfIncorp(data.countryOfIncorporation || "");
  setters.setCountryCountryOfDomicile(data.countryOfDomicile || "");
  setters.setAddress(data.legalEntityAddress || "");
  setters.setPostalCode(data.postalCode?.toString() || "");
  setters.setcompanysite(data.website || "");
  setters.setPhone(data.phoneNumber || "");
  setters.setCompanyStartDate(data.companyStartDate || "");
  setters.setCountryCode(
    (codeToCountryNameMap[
      data.countryOfDomicile as keyof typeof codeToCountryNameMap
    ] as CountryCode) || undefined
  );
};

/**
 * Formats date from API to ISO format for date input
 */
export const formatDateForInput = (dateString: string): string => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "";
  // Return empty string for Unix epoch year (1970)
  if (date.getFullYear() === 1970) return "";
  return date.toISOString().split("T")[0];
};

/**
 * Formats phone number to ensure it starts with +
 */
export const formatPhoneNumber = (phoneNumber: string): string => {
  return phoneNumber.startsWith("+") ? phoneNumber : `+${phoneNumber}`;
};

/**
 * Validates all required fields in the business profile form
 */
export const validateBizProfileForm = (formState: {
  companyName: string;
  registrationNumber: string;
  companyStartDate: string;
  legalStructure: string;
  industryGroup: string;
  institutionType: string;
  countryOfIncorporation: string;
  countryOfDomicile: string;
  address: string;
  phoneNumber: string;
  postalcode: string;
  companyWebsite: string;
}): { valid: boolean; errors: BizProfileFormErrors } => {
  const errors = { ...INITIAL_ERRORS };
  let valid = true;

  // Required fields to validate (excluding optional fields)
  const requiredFields: Array<keyof typeof formState> = [
    "companyName",
    "registrationNumber",
    "companyStartDate",
    "legalStructure",
    "industryGroup",
    "countryOfIncorporation",
    "countryOfDomicile",
    "address",
    "phoneNumber",
    "postalcode",
    "companyWebsite",
  ];

  requiredFields.forEach((field) => {
    if (!formState[field]) {
      valid = false;
      const errorKey =
        field === "address"
          ? "address"
          : field === "postalcode"
          ? "postalcode"
          : field === "companyWebsite"
          ? "companyWebsite"
          : field;
      errors[errorKey as keyof BizProfileFormErrors] =
        REQUIRED_FIELD_ERRORS[field] || `${field} is required`;
    }
  });

  // Special validation for industry
  if (!formState.institutionType) {
    valid = false;
    errors.institutionType = REQUIRED_FIELD_ERRORS.institutionType;
  }

  return { valid, errors };
};
